export const MONITORING_TYPE_LABELS = {
  bloodPressure: 'Blood Pressure',
  bloodGlucose: 'Blood Glucose',
  weight: 'Weight',
};

export const MONITORING_LABEL_TO_TYPE_KEY = {
  'Blood Pressure': 'bloodPressure',
  'Blood Glucose': 'bloodGlucose',
  Weight: 'weight',
};

export const MONITORING_LABEL_TO_MEASURE_TYPE_ID = {
  'Blood Pressure': 1,
  'Blood Glucose': 2,
  Weight: 3,
};

export const DEFAULT_MONITORING_ICD_VALUES = {
  'Blood Pressure': '',
  'Blood Glucose': '',
  Weight: '',
};

export function getIcdOptionCode(option) {
  if (option == null) return '';
  if (typeof option === 'string') return option.trim();
  return String(option.code || '').trim();
}

export function formatIcdOptionLabel(option) {
  if (option == null) return '';
  if (typeof option === 'string') return option;
  const code = String(option.code || '').trim();
  const name = String(option.name || '').trim();
  if (code && name) return `${code} — ${name}`;
  return code || name;
}

export function icdOptionsIncludeCode(options = [], code) {
  const needle = String(code || '').trim();
  if (!needle) return false;
  return options.some((option) => getIcdOptionCode(option) === needle);
}

export function findIcdOptionByCode(options = [], code) {
  const needle = String(code || '').trim();
  if (!needle) return null;
  return options.find((option) => getIcdOptionCode(option) === needle) || null;
}

export function filterIcdOptions(options = [], query = '') {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return options;
  return options.filter((option) => {
    const code = getIcdOptionCode(option).toLowerCase();
    const name = String(option?.name || '').toLowerCase();
    return code.startsWith(q) || code.includes(q) || name.includes(q);
  });
}

export function mergeIcdSearchResults(primary = [], secondary = []) {
  const seen = new Set();
  const merged = [];
  [...primary, ...secondary].forEach((option) => {
    const code = getIcdOptionCode(option);
    if (!code || seen.has(code)) return;
    seen.add(code);
    merged.push(typeof option === 'string' ? { code, name: '' } : option);
  });
  return merged;
}

export function emptyMonitoringIcdValues() {
  return {
    'Blood Pressure': '',
    'Blood Glucose': '',
    Weight: '',
  };
}

/** Parse ICD lookup rows into { 1: [{code,name}], 2: [...], 3: [...] }. */
export function groupIcdCodesByMeasureType(rows = []) {
  const grouped = { 1: [], 2: [], 3: [] };
  rows.forEach((row) => {
    const typeId = Number(row?.measure_type_id);
    if (!grouped[typeId]) return;

    if (row?.code) {
      grouped[typeId].push({
        code: String(row.code).trim(),
        name: String(row.name || '').trim()
      });
      return;
    }

    String(row?.codes || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean)
      .forEach((code) => grouped[typeId].push({ code, name: '' }));
  });

  Object.keys(grouped).forEach((key) => {
    const seen = new Set();
    grouped[key] = grouped[key].filter((item) => {
      const code = getIcdOptionCode(item);
      if (!code || seen.has(code)) return false;
      seen.add(code);
      return true;
    });
  });
  return grouped;
}

export function getIcdOptionsForMonitoringLabel(label, icdCodesByMeasureType = {}) {
  const typeId = MONITORING_LABEL_TO_MEASURE_TYPE_ID[label];
  if (!typeId) return [];
  return icdCodesByMeasureType[typeId] || [];
}

function resolveMonitoringLabelFromRawType(rawType) {
  if (rawType == null || rawType === '') return null;
  const key = String(rawType).trim().toLowerCase();
  if (key === 'bloodpressure' || key === 'blood_pressure' || key === 'bp' || key === '1') {
    return MONITORING_TYPE_LABELS.bloodPressure;
  }
  if (key === 'bloodglucose' || key === 'blood_glucose' || key === 'bg' || key === 'glucose' || key === '2') {
    return MONITORING_TYPE_LABELS.bloodGlucose;
  }
  if (key === 'weight' || key === 'wt' || key === '3') {
    return MONITORING_TYPE_LABELS.weight;
  }
  if (key === 'blood pressure') return MONITORING_TYPE_LABELS.bloodPressure;
  if (key === 'blood glucose') return MONITORING_TYPE_LABELS.bloodGlucose;
  return null;
}

/** Normalize patient monitoring_types + monitoring_values into labels and ICD map. */
export function parsePatientMonitoringSelection(patient = {}) {
  const labels = [];
  const icdValues = emptyMonitoringIcdValues();

  const applyLabel = (label, icdCode) => {
    if (!label || labels.includes(label)) return;
    labels.push(label);
    if (icdCode != null && String(icdCode).trim() !== '') {
      icdValues[label] = String(icdCode).trim();
    }
  };

  let rawMon = patient.monitoring_types || patient.monitoringTypes || [];
  if (rawMon && typeof rawMon === 'object' && !Array.isArray(rawMon)) {
    if (rawMon.bloodPressure || rawMon.blood_pressure) {
      applyLabel(
        MONITORING_TYPE_LABELS.bloodPressure,
        patient.monitoring_values?.bloodPressure || patient.monitoring_values?.blood_pressure
      );
    }
    if (rawMon.bloodGlucose || rawMon.blood_glucose) {
      applyLabel(
        MONITORING_TYPE_LABELS.bloodGlucose,
        patient.monitoring_values?.bloodGlucose || patient.monitoring_values?.blood_glucose
      );
    }
    if (rawMon.weight) {
      applyLabel(
        MONITORING_TYPE_LABELS.weight,
        patient.monitoring_values?.weight
      );
    }
    return { labels, icdValues };
  }

  const list = Array.isArray(rawMon)
    ? rawMon
    : (typeof rawMon === 'string' ? rawMon.split(',').map((item) => item.trim()).filter(Boolean) : []);

  list.forEach((item) => {
    if (typeof item === 'string') {
      applyLabel(item, null);
      return;
    }
    if (item && typeof item === 'object') {
      const label = resolveMonitoringLabelFromRawType(item.type ?? item.measured_type_id ?? item.measuredTypeId);
      if (label) {
        applyLabel(label, item.icdCode || item.icd_code || null);
      }
    }
  });

  const monitoringValues = patient.monitoring_values || patient.monitoringValues || {};
  if (monitoringValues.bloodPressure && labels.includes(MONITORING_TYPE_LABELS.bloodPressure) && !icdValues[MONITORING_TYPE_LABELS.bloodPressure]) {
    icdValues[MONITORING_TYPE_LABELS.bloodPressure] = monitoringValues.bloodPressure;
  }
  if (monitoringValues.bloodGlucose && labels.includes(MONITORING_TYPE_LABELS.bloodGlucose) && !icdValues[MONITORING_TYPE_LABELS.bloodGlucose]) {
    icdValues[MONITORING_TYPE_LABELS.bloodGlucose] = monitoringValues.bloodGlucose;
  }
  if (monitoringValues.weight && labels.includes(MONITORING_TYPE_LABELS.weight) && !icdValues[MONITORING_TYPE_LABELS.weight]) {
    icdValues[MONITORING_TYPE_LABELS.weight] = monitoringValues.weight;
  }

  return { labels, icdValues };
}

/** Build API monitoring_types payload with per-vital ICD codes. */
export function buildMonitoringTypesPayload(selectedLabels = [], icdValuesByLabel = {}) {
  return [...new Set(selectedLabels)].map((label) => {
    const typeKey = MONITORING_LABEL_TO_TYPE_KEY[label] || label;
    const icdCode = icdValuesByLabel[label] || null;
    return { type: typeKey, icdCode };
  });
}

export function resolveDefaultMonitoringIcd(label, options = []) {
  const currentDefault = DEFAULT_MONITORING_ICD_VALUES[label];
  if (currentDefault && icdOptionsIncludeCode(options, currentDefault)) return currentDefault;
  return getIcdOptionCode(options[0]) || currentDefault || '';
}
